import { Card, Alert, Form, Slider, Button, Space } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useStoreActions, useStoreState } from '../../../store';
import DatePicker from '../../dateFnsComponents/DatePicker';
import { format, endOfDay, startOfDay, subDays, addDays, parse } from 'date-fns';
import { HistoricalShipmentTable } from './HistoricalShipmentTable';
import { BASE_DETAILS_WORKFLOW_KEY } from './SimulationBaseDetailsStep';
import { HISTORICAL_DATASET_MODE, SHIPMENT_SELECTION_KEY } from './SimulationShipmentSelectStep';
import { useSearchParams } from 'react-router-dom-v5-compat';
import type { HistoricalShipmentSelectData, SimulationSetupData } from '../../../store/simulation/types';
import { SimulationWorkflowCard } from './SimulationWorkflowCard';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { SimulationUploadDateValidator } from './UsePreviousHistoricalShipments';
import { OriginSelectModal, ORIGIN_SELECT_MODAL_KEY } from './OriginSelectModal';
import { TenantSelectModal, TENANT_SELECT_MODAL_KEY } from './TenantSelectModal';
import { BaseFeatureToggles } from '../../../store/navigation/BaseFeatureToggles';
import { useShipmentSizeAlert } from './ShipmentSizeAlert';
import { useSimulationVersion } from '~/hooks/simulation/useSimulationVersion';
import { useUpdateSimulation } from '~/hooks/simulation/useSimulationSetupMutations';
import {
  HISTORICAL_DATASET_LOOKBACK_MESSAGE,
  historicalDatasetLookbackValidator,
  isBeforeHistoricalDatasetLookback,
} from './historicalDatasetDateLimits';
const { RangePicker } = DatePicker;
const { useWatch } = Form;
export const HistoricalSimulationSelect = () => {
  const fetchCarrierSelectionSummaries = useStoreActions(
    (actions) => actions.simulation.fetchCarrierSelectionSummaries,
  );
  const { mutateAsync: updateSimulation } = useUpdateSimulation();
  const activeNavigationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  const setActiveNavigationStep = useStoreActions((state) => state.simulation.setActiveNavigationStep);
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const simulations = useStoreState((state) => state.simulation.simulationDetails);
  const updateSimulationSetup = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const carrierSelectionSummaries = useStoreState((state) => state.simulation.carrierSelectionSummaries);
  const modalState = useStoreState((state) => state.navigation.modals);
  const setModalState = useStoreActions((actions) => actions.navigation.toggleModal);
  const checkFeatureToggle = useStoreState((state) => state.partner.checkFeatureToggle);
  const usingTenant = checkFeatureToggle(BaseFeatureToggles.TENANT);
  const isV1 =
    useStoreState((state) => state.partner.activePartner?.featureConfigurationInfo?.megaProcessVersion) === '1';
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dateRangeState, setDateRangeState] = useState<any>([]);
  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const { version: serviceVersion } = useSimulationVersion(simulationId);
  const samplingPercentage = useWatch('samplingPercentage', form);
  const [selectedOrigins, setSelectedOrigins] = useState<Record<string, boolean>>({});
  const [selectedTenants, setSelectedTenants] = useState<Record<string, boolean>>({});
  const selectedTenantIds = useMemo(
    () =>
      Object.entries(selectedTenants ?? {}).reduce(
        (prev: string[], val: [string, boolean]) => (val[1] ? [...prev, val[0]] : prev),
        [],
      ) ?? [],
    [selectedTenants],
  );
  const selectedOriginIds = useMemo(
    () =>
      Object.entries(selectedOrigins ?? {}).reduce(
        (prev: string[], val: [string, boolean]) => (val[1] ? [...prev, val[0]] : prev),
        [],
      ) ?? [],
    [selectedOrigins],
  );
  const activeSimulation = simulations[simulationId ?? ''];
  const handleSubmit = async () => {
    try {
      await form.validateFields(['dateRange']);
    } catch {
      return;
    }
    const { dateRange } = form.getFieldsValue();
    const [start, end] = dateRange ?? [];
    if (start && end) {
      setLoading(true);
      fetchCarrierSelectionSummaries({
        tenantIds: selectedTenantIds,
        originIds: selectedOriginIds,
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
      }).finally(() => {
        setLoading(false);
      });
    }
  };
  const disabledDate = (current: Date) => {
    if (isBeforeHistoricalDatasetLookback(current)) {
      return true;
    }
    // the one-month span cap is v1-only (lifted for v2 in PIP-1065)
    if (serviceVersion === 'v2') {
      return false;
    }
    // 32 gives us TZ wiggle room for a month idk
    // maybe unnecessary
    if (dateRangeState && !!dateRangeState[1] && subDays(dateRangeState[1], 32) > current) {
      return true;
    }
    if (dateRangeState && !!dateRangeState[0] && addDays(dateRangeState[0], 32) < current) {
      return true;
    }
    return false;
    // return current > moment() || current < moment().subtract(3, 'day');
  };
  const totalShipments = useMemo(() => {
    return carrierSelectionSummaries?.reduce((total, summary) => total + summary.carrierSelectionCount, 0) ?? 0;
  }, [carrierSelectionSummaries]);
  const shipmentsToSimulate = Math.floor(totalShipments * (samplingPercentage / 100));
  const { exceededMaxShipments, ShipmentSizeAlert } = useShipmentSizeAlert({ totalShipments: shipmentsToSimulate });
  useEffect(() => {
    if (simulationId && simulationSetupData[SHIPMENT_SELECTION_KEY]) {
      const { end, start, samplingPercentage } = simulationSetupData[
        SHIPMENT_SELECTION_KEY
      ] as HistoricalShipmentSelectData;
      const parsedStart = parse(start, 'yyyy-MM-dd', new Date());
      const parsedEnd = parse(end, 'yyyy-MM-dd', new Date());
      const dateRange = [parsedStart, parsedEnd];
      form.setFieldsValue({
        dateRange,
        samplingPercentage: Number(samplingPercentage ?? 0),
      });
    }
  }, [simulationId, simulationSetupData, form]);

  useEffect(() => {
    const savedOriginIds = (simulationSetupData[SHIPMENT_SELECTION_KEY] as HistoricalShipmentSelectData)?.originIds;
    if (savedOriginIds?.length) {
      const init: Record<string, boolean> = {};
      for (const val of savedOriginIds) {
        init[val] = true;
      }
      setSelectedOrigins(init);
    }
  }, [simulationSetupData, setSelectedOrigins]);

  useEffect(() => {
    const savedTenantIds = (simulationSetupData[SHIPMENT_SELECTION_KEY] as HistoricalShipmentSelectData)?.tenantIds;
    if (savedTenantIds?.length) {
      const init: Record<string, boolean> = {};
      for (const val of savedTenantIds) {
        init[val] = true;
      }
      setSelectedTenants(init);
    }
  }, [simulationSetupData, setSelectedTenants]);

  const tableData = useStoreState((state) => state.simulation.carrierSelectionTableSummaries);
  const handleSave = async () => {
    form
      .validateFields()
      .then(async () => {
        const { dateRange, samplingPercentage } = form.getFieldsValue();
        const [start, end] = dateRange;
        const formattedStart = format(startOfDay(start), 'yyyy-MM-dd');
        const formattedEnd = format(endOfDay(end), 'yyyy-MM-dd');
        const modifiedSetupData: SimulationSetupData = {
          ...simulationSetupData,
          [SHIPMENT_SELECTION_KEY]: {
            mode: HISTORICAL_DATASET_MODE,
            carrierSelectionSummaries,
            start: formattedStart,
            end: formattedEnd,
            samplingPercentage,
            originIds: selectedOriginIds,
            tenantIds: selectedTenantIds,
          },
        };
        updateSimulationSetup(modifiedSetupData);
        // add origin list to shipment options
        const currentShipmentOptions = activeSimulation.simulationShipmentOptions ?? {};
        if (simulationId) {
          await updateSimulation({
            ...simulationSetupData[BASE_DETAILS_WORKFLOW_KEY],
            simulationId,
            includesHistoricalSelection: true,
            simulationSourceStartDate: formattedStart,
            simulationSourceEndDate: formattedEnd,
            samplingPercentage,
            simulationSetupData: modifiedSetupData,
            simulationShipmentOptions: {
              ...currentShipmentOptions,
              ...(selectedOriginIds?.length ? { originIds: selectedOriginIds } : {}),
              ...(selectedTenantIds?.length ? { tenantIds: selectedTenantIds } : {}),
            },
          });
        }
        setActiveNavigationStep(activeNavigationStep + 1);
      })
      .catch((err) => {
        console.log('err', err);
      });
  };
  return (
    <div>
      <OriginSelectModal
        selectedOrigins={selectedOrigins}
        onSelectedOriginsChange={setSelectedOrigins}
        open={modalState[ORIGIN_SELECT_MODAL_KEY]?.open ?? false}
      />
      <TenantSelectModal
        selectedTenants={selectedTenants}
        onSelectedTenantsChange={setSelectedTenants}
        open={modalState[TENANT_SELECT_MODAL_KEY]?.open ?? false}
      />
      <Card loading={loading}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert
            message="Use Historical Shipium Dataset"
            description="Use your own historical carrier selections for a given date range for the simulation dataset.  You will have the option of selecting a percentage of the total historical shipments to simulate."
            type="info"
            showIcon
          />
          <ShipmentSizeAlert />
        </Space>

        <div style={{ marginTop: 30 }}>
          <Form form={form} layout={'vertical'}>
            <Space size={40}>
              <Form.Item
                rules={[
                  { required: true },
                  { validator: historicalDatasetLookbackValidator() },
                  ...(serviceVersion === 'v2' ? [] : [{ validator: SimulationUploadDateValidator }]),
                ]}
                required={true}
                name="dateRange"
                label="Shipment Created Date Range"
                tooltip={HISTORICAL_DATASET_LOOKBACK_MESSAGE}
              >
                <RangePicker onCalendarChange={setDateRangeState} disabledDate={disabledDate} showTime={false} />
              </Form.Item>
              {!isV1 && (
                <Form.Item
                  tooltip={
                    'Optionally select a set of origins to filter shipments by. If none are selected, shipments will not be filtered by origin'
                  }
                  label="Filter Shipments by Origin"
                >
                  <Button
                    onClick={() => {
                      setModalState({ key: ORIGIN_SELECT_MODAL_KEY, open: true });
                    }}
                  >
                    {selectedOriginIds.length > 0
                      ? `${selectedOriginIds.length} origin(s) selected`
                      : 'No origins selected'}{' '}
                    <PlusCircleOutlined />
                  </Button>
                </Form.Item>
              )}
              {usingTenant && (
                <Form.Item
                  tooltip={
                    'Optionally select a set of tenants to filter shipments by. If none are selected, shipments will not be filtered by tenant'
                  }
                  label="Filter Shipments by Tenant"
                >
                  <Button
                    onClick={() => {
                      setModalState({ key: TENANT_SELECT_MODAL_KEY, open: true });
                    }}
                  >
                    {selectedTenantIds.length > 0
                      ? `${selectedTenantIds.length} tenant(s) selected`
                      : 'No tenants selected'}
                    <PlusCircleOutlined />
                  </Button>
                </Form.Item>
              )}
              <Form.Item style={{ marginLeft: 10, marginBottom: '-6px' }}>
                <Button onClick={handleSubmit} type="primary">
                  Load
                </Button>
              </Form.Item>
              <Form.Item
                style={{ marginLeft: 30, width: 300, marginBottom: 0 }}
                hidden={carrierSelectionSummaries.length < 1}
                initialValue={100}
                required={true}
                extra={`${shipmentsToSimulate.toLocaleString()} shipments to simulate`}
                tooltip={
                  'Use the sampling percentage to reduce the number of shipments to run for your simulation. This will help you get your results faster'
                }
                name="samplingPercentage"
                label="Shipment Sampling Percentage"
              >
                <Slider tooltip={{ open: true, placement: 'right' }} defaultValue={100} min={1} />
              </Form.Item>
            </Space>
          </Form>
        </div>
      </Card>
      <SimulationWorkflowCard
        simulationId={simulationId}
        onSave={handleSave}
        disabled={carrierSelectionSummaries.length < 1 || exceededMaxShipments}
      >
        <HistoricalShipmentTable data={tableData ?? []} samplingPercentage={samplingPercentage} />
      </SimulationWorkflowCard>
    </div>
  );
};
